/**
 * Entity Details panel — the consolidated detail surface per
 * ADR-024 §4 + Flow Q. Replaces the M5 right-slide-in detail
 * pattern from Flow 04 with a dockable, scrollable single panel.
 *
 * Stream A PR 5.3 ships the **structure** + the Platform-kind
 * section schema. The Aggregator + Event section schemas land in
 * PR 5.5; the section-visibility menu lands in PR 5.4.
 *
 * # Behavior
 *
 * Subscribes to the selection store's ``inspected`` slot (the operator's
 * reading focus — 2026-06-09 two-slot redesign; a Platforms/Detections/
 * Alerts list left-click moves this without disturbing the map ring).
 * Renders one of these states (the data-plane Event kind was removed
 * in the ADR-032 cascade — PR6; Detection + Alert supersede it):
 *
 *   1. **No selection** → empty state with the Flow Q §"Frame 5"
 *      message.
 *   2. **Platform selection** → header + scrollable body of
 *      Platform sections.
 *   3. **Aggregator selection** → real AggregatorSections.
 *   4. **Detection / Alert selection** → real sections
 *      (DetectionSections / AlertSections), populated from the PR4
 *      detail endpoints. PR5a increment 4 replaced the inc-1
 *      placeholder with these real Detection (ADR-032 §4) / Alert
 *      (ADR-029 / ADR-032 §4.2) section schemas.
 *
 * The panel renders no DOM chrome (no Close X) — dockview owns the
 * tab header. Per ADR-024 §4 the "section visibility gear" is the only
 * panel-level affordance; it landed platform-only in PR 5.4 and now
 * appears for **every** entity type (platform / aggregator / detection /
 * alert) per REQ-UI-092 — each header passes its ``entityType`` to
 * :func:`SectionVisibilityMenu`, and sections are additionally
 * collapsible per REQ-UI-064.
 *
 * # ⚠ Every scroll body here is ``min-h-0 flex-1 overflow-y-auto``
 *
 * The ``min-h-0`` is load-bearing and its absence is invisible until the
 * content gets tall. In a column flex container ``flex-1`` resolves to
 * ``flex: 1 1 0%`` but leaves ``min-height: auto``, so the item's used
 * minimum is its **content's** min-content height and the body cannot
 * shrink. It is the idiom the rest of the UI already uses
 * (``provider-roster``, ``peer-roster``, ``data-table``, ``query-panel``,
 * ``status-connection-list``); these seven were the outliers. Guarded by
 * ``entity-details-panel.test.tsx``.
 *
 * ## ⚠ This is NOT the fix for the 2026-10-10 scroll report
 *
 * Recorded because the two look identical and the wrong one is the
 * obvious one. The report was that scrolling this pane with the SOURCE
 * BYTES drawer open pushed its dockview tab up under the top nav bar.
 * ``min-h-0`` looks exactly like that cause — a flex child that cannot
 * shrink should overflow its panel — and it is not. Measured on a real
 * engine with the drawer open at viewport heights 900, 720, 640, 560,
 * 480, 420 and 360 px: ``panelH === groupH`` and ``overflowPx: 0`` at
 * **every** one, with and without ``min-h-0``. The panel never exceeds
 * its group, so nothing overflows to an ancestor.
 *
 * What actually happens is scroll **chaining**: at the body's scroll end
 * the wheel reaches dockview's own ``.dv-view``, an ``overflow-y: auto``
 * wrapper around the whole group, **tab strip included**. Measured
 * before/after a sustained wheel — ``.dv-view`` ``scrollTop`` 0 → 605 and
 * ``.dv-groupview`` rect ``y`` 48 → -557, while
 * ``document.scrollingElement.scrollTop`` stays 0. The page never
 * scrolls, which is why every page-level check reads clean.
 *
 * ``overscroll-behavior: contain`` on this body ends the chain (verified:
 * afterwards no ancestor's ``scrollTop`` moves and the body's own rect
 * stays put). **That change is deliberately not here** — it is owned by
 * the concurrent track-display UI work, because the chaining is a
 * dockview-wide property rather than this panel's and is better fixed
 * once, at the theme level, for every panel.
 *
 * Cross-references:
 *   - REQ-UI-049 (consolidated Entity Details panel structure).
 *   - REQ-UI-051 (selection-driven population).
 *   - REQ-UI-066 (Detection sections) / REQ-UI-067 (Alert sections).
 *   - REQ-UI-092 (per-type section visibility + expand/collapse) /
 *     REQ-UI-064 (collapsible sections, all entity types).
 *   - Flow Q §"Frame 2" / §"Frame 3" / §"Frame 5".
 *   - Flow 04 §"Panel content per entity type".
 *   - ADR-024 §4 / ADR-032 (Detection) / ADR-029 (Alert) / ADR-033 §10.
 */
import {
  useInspected,
  useNavigationBackTarget,
  useSelectionActions,
  type StatusConnectionSelection,
  type StatusInterfaceSelection,
  type StatusPeerSelection,
} from "@/selection/selection-store";
import { shortId } from "@/lib/short-id";
import { useProducerNames } from "@/lib/use-producer-names";
import { useAnnotationToolStore } from "@/annotations/annotation-tool-store";
import { AnnotationDetailPanel } from "@/entity-details/annotation-detail-panel";
import { describeSelection } from "@/entity-details/selection-label";
import { AggregatorSections } from "@/entity-details/aggregator-sections";
import { entityKindForRecordType, type EntityKind } from "@/entity-details/entity-record-mapping";
import { AlertSections } from "@/entity-details/alert-sections";
import { DetectionSections } from "@/entity-details/detection-sections";
import { TrackSections } from "@/entity-details/track-sections";
import { PlatformSections } from "@/entity-details/platform-sections";
import { useProducerDetail } from "@/entity-details/use-producer-detail";
import { SectionVisibilityMenu } from "@/entity-details/section-visibility-menu";
import { RecordSections } from "@/query/record-sections";
import { StatusComponentSections } from "@/entity-details/status-component-sections";
import { StatusConnectionSections } from "@/entity-details/status-connection-sections";
import { StatusInterfaceSections } from "@/entity-details/status-interface-sections";
import { StatusPeerSections } from "@/entity-details/status-peer-sections";
import { PeerManagementDialog } from "@/peers/peer-management-dialog";
import { PEER_MUTATOR_ROLES } from "@/peers/use-peer-interfaces";
import { useSession } from "@/auth/session-context";
import {
  componentRosterId,
  interfaceComponent,
  joinPeerInterfaces,
  primaryComponent,
} from "@/entity-details/status-detail-scoping";
import { useResolvedSystemStatus } from "@/system-status/use-resolved-status-model";
import { resolveNodeRender } from "@/system-status/status-node-render";
import { StatusPill } from "@/system-status/status-detail-primitives";
import { useComponentConnections } from "@/system-status/use-component-connections";
import {
  parseRollupState,
  type ResolvedComponent,
  type RollupState,
} from "@/system-status/status-model";
import { formatStatusTimestamp } from "@/system-status/status-connection-format";
import {
  peerComponentId,
  TOPOLOGY_NODES,
  type ConfiguredInterface,
} from "@/system-status/status-topology";
import { useState } from "react";
import { CaretLeft } from "@phosphor-icons/react";
import { usePeerInterfaces } from "@/peers/use-peer-interfaces";
import { withEnabled } from "@/peers/peer-model";
import {
  useDeleteNestedInterface,
  useDeletePeer,
  useExportPeerBundle,
  usePeerInterfacesFor,
  usePeers,
  useSaveNestedInterface,
} from "@/peers/use-peers";

export function EntityDetailsPanel(): React.ReactElement {
  // A selected map annotation takes precedence over the inspected entity — the
  // operator focuses one thing at a time (ADR-034 design update: annotation
  // config lives here, not in a floating editor). The annotation selection is
  // its own store (it survives the entity background-deselect that fires on a
  // non-entity click), and clicking an entity clears it, so in practice the two
  // are mutually exclusive.
  const annotationId = useAnnotationToolStore((state) => state.selectedId);

  // Entity Details renders the ``inspected`` slot — the operator's reading
  // focus — which a list-row left-click moves independently of the map ring
  // (the 2026-06-09 two-slot redesign; see selection-store §"The two slots").
  const selected = useInspected();

  if (annotationId !== null) {
    return <AnnotationDetailPanel id={annotationId} />;
  }
  if (selected === null) {
    return <EmptyState />;
  }
  if (
    selected.kind === "platform" ||
    selected.kind === "aggregator" ||
    selected.kind === "detection" ||
    selected.kind === "alert" ||
    selected.kind === "track"
  ) {
    return <EntityPane kind={selected.kind} id={selected.id} />;
  }
  if (selected.kind === "query-record") {
    const entityKind = entityKindForRecordType(selected.recordType);
    if (entityKind !== null) {
      // A Query result row whose type IS an entity renders that entity's own
      // sections — the same pane a recent-data list row opens (REQ-UI-183).
      // ADR-041 D-6 did this for ``sapient_track`` alone; 2026-10-05
      // generalized it to every mapped type, so the two routes to one record
      // can no longer drift apart. ``origin`` only annotates the pane; the
      // sections themselves are identical.
      return <EntityPane kind={entityKind} id={selected.id} origin="query-record" />;
    }
    // A type with no entity counterpart — the status samples, raw blobs, the
    // system-status types (ADR-038 §Decision 5 / REQ-UI-123). The type id
    // travels with the record id because both are needed to address the BFF's
    // detail route. The section-visibility gear is deliberately absent here:
    // its per-type config (ADR-033 §10) is keyed by entity type, and these
    // records have none — which is exactly the distinction that decides this
    // branch, rather than a list of type names to keep in sync.
    return (
      <div
        data-testid="entity-details-panel"
        data-kind="query-record"
        role="region"
        aria-label="Record details"
        className="flex h-full w-full flex-col bg-bg-base"
      >
        <Header title={selected.id} subtitle={`Record · ${selected.recordType}`} />
        <div data-testid="entity-details-body" className="min-h-0 flex-1 overflow-y-auto">
          <RecordSections typeId={selected.recordType} recordId={selected.id} />
        </div>
      </div>
    );
  }
  if (selected.kind === "status-component") {
    // A System Status topology node (ADR-024 §4 amended 2026-07-24 — was an
    // in-panel rail). Detail resolves from the live System Status feed
    // (:func:`useResolvedSystemStatus`), not a per-id BFF route; the
    // section-visibility gear is absent (like query-record — decision 2026-07-24).
    return <StatusComponentDetailPanel nodeId={selected.id} />;
  }
  if (selected.kind === "status-peer") {
    // One peer icon on the Peer Manager's stack — the level ADR-039 §D-1 inserted
    // between the component and the interface (ADR-024 §4 amended 2026-08-05;
    // REQ-STATUS-081 / -086). Not a topology node, so it carries
    // (parentNodeId, peerName) for the same reason an interface does.
    return <StatusPeerDetailPanel selection={selected} />;
  }
  if (selected.kind === "status-interface") {
    // One interface icon on an Ingress/Peer Manager node — the middle level of the
    // component → interface → connection hierarchy (ADR-024 §4 amended
    // 2026-07-31; REQ-STATUS-039). Not a topology node, so it carries
    // (parentNodeId, interfaceName) rather than an id that would miss against
    // TOPOLOGY_NODES and render a silently empty panel.
    return <StatusInterfaceDetailPanel selection={selected} />;
  }
  if (selected.kind === "status-connection") {
    // A drilled-in System Status connection (ADR-024 §5 amended). Composite
    // identity ``(componentId, id)`` + the originating ``nodeId`` for back-nav.
    //
    // Under the 2026-07-31 hierarchy a roster row expands to a **drawer** rather
    // than promoting the connection to its own focus, so nothing in the UI
    // originates this kind today. The branch is kept rather than deleted: the
    // kind is part of a deployed v1.0.0 contract (additive-only, CLAUDE.md
    // §4.2), a persisted layout or a deep link may still carry one, and
    // rendering an EmptyState for a selection the bus still accepts would be the
    // silent-empty-panel failure REQ-STATUS-039 exists to prevent.
    return <StatusConnectionDetailPanel selection={selected} />;
  }
  // Exhaustiveness guard: ``selected.kind`` is now narrowed to
  // ``never`` (every SelectionKind has a returning branch above). If a
  // future SelectionKind is added without a branch here, this
  // assignment fails to type-check — surfacing the gap at compile time
  // rather than rendering a blank panel at runtime.
  const _exhaustive: never = selected.kind;
  void _exhaustive;
  return <EmptyState />;
}

/** Where the operator reached an entity from, when that is worth saying. */
type PaneOrigin = "query-record";

/**
 * The pane for one entity kind — **the** pane for it, whichever surface
 * the operator arrived through (REQ-UI-183).
 *
 * Until 2026-10-05 each kind had its own branch in the panel's dispatch
 * and a Query result row of the same record had a *different* branch
 * rendering the generic attribute list, so a detection found by query
 * showed none of the classification, signal, behaviour or provenance a
 * Detections-panel click had always shown. Collapsing both routes onto
 * one renderer makes the consistency structural: there is no longer a
 * second place for a kind's pane to be defined, so the two cannot drift.
 *
 * ``origin`` annotates the subtitle and sets ``data-origin`` — the
 * operator keeps the cue about where the selection came from — and
 * changes nothing else. Query rows still never push to the map (ADR-038
 * §Decision 5 / ADR-024 §5); that is enforced by the bus's types, not
 * here.
 */
function EntityPane({
  kind,
  id,
  origin,
}: {
  kind: EntityKind;
  id: string;
  origin?: PaneOrigin;
}): React.ReactElement {
  // Platform is its own component so ``useProducerDetail`` (which resolves
  // the friendly registration name for the title) is called unconditionally
  // rather than inside a kind switch — rules of hooks.
  if (kind === "platform") {
    return <PlatformDetailPanel producerId={id} origin={origin} />;
  }
  const descriptor = ENTITY_PANE_DESCRIPTORS[kind];
  return (
    <div
      data-testid="entity-details-panel"
      data-kind={kind}
      data-origin={origin}
      role="region"
      aria-label={descriptor.ariaLabel}
      className="flex h-full w-full flex-col bg-bg-base"
    >
      <Header
        title={descriptor.title(id)}
        subtitle={paneSubtitle(descriptor.subtitle, origin)}
        actions={<SectionVisibilityMenu entityType={kind} />}
      />
      <div data-testid="entity-details-body" className="min-h-0 flex-1 overflow-y-auto">
        {descriptor.sections(id)}
      </div>
    </div>
  );
}

/**
 * Per-kind pane chrome: the header title, the subtitle, the region's
 * accessible name, and which sections component fills the body.
 *
 * A table rather than a switch so adding a kind is one entry and the
 * compiler names the gap — ``Record<EntityKind, …>`` minus the platform
 * kind, which :func:`EntityPane` handles separately for the hook reason
 * above.
 */
const ENTITY_PANE_DESCRIPTORS: Readonly<
  Record<
    Exclude<EntityKind, "platform">,
    {
      title: (id: string) => string;
      subtitle: string;
      ariaLabel: string;
      sections: (id: string) => React.ReactElement;
    }
  >
> = {
  aggregator: {
    title: (id) => id,
    subtitle: "Aggregator",
    ariaLabel: "Aggregator details",
    sections: (id) => <AggregatorSections aggregatorId={id} />,
  },
  detection: {
    // Short, glanceable handle; the full id stays on the IDENTITY row.
    title: (id) => shortId(id, "DET"),
    subtitle: "Detection",
    ariaLabel: "Detection details",
    sections: (id) => <DetectionSections reportId={id} />,
  },
  alert: {
    title: (id) => shortId(id, "ALT"),
    subtitle: "Alert",
    ariaLabel: "Alert details",
    sections: (id) => <AlertSections alertId={id} />,
  },
  track: {
    title: (id) => shortId(id, "TRK"),
    subtitle: "Track",
    ariaLabel: "Track details",
    sections: (id) => <TrackSections trackId={id} />,
  },
};

/** The subtitle, with the provenance cue when there is one. */
function paneSubtitle(base: string, origin: PaneOrigin | undefined): string {
  return origin === "query-record" ? `${base} · query result` : base;
}

/**
 * Platform Entity Details container. Owns the producer-detail fetch for
 * the **primary header title** — the friendly registration ``name``
 * (falling back to ``short_name``, then the producer id) per REQ-UI-065.
 * The fetch shares ``useProducerDetail``'s query key with
 * :func:`PlatformSections`, so TanStack Query dedups it to one request.
 * Kept as a dedicated component so the hook is called unconditionally
 * (rules-of-hooks) rather than inside the panel's kind switch.
 */
function PlatformDetailPanel({
  producerId,
  origin,
}: {
  producerId: string;
  origin?: PaneOrigin;
}): React.ReactElement {
  const registration = useProducerDetail({ producerId }).data?.registration ?? null;
  // Fall back to the producers-list display name (the friendly identifier the
  // Platforms list shows; REQ-UI-065) rather than the raw producer UUID when a
  // platform has no registration name/short_name — the common
  // status-discovered case the operator was seeing as a ULID at the top.
  const names = useProducerNames();
  const title = registration?.name || registration?.short_name || names.nameFor(producerId);
  return (
    <div
      data-testid="entity-details-panel"
      data-kind="platform"
      data-origin={origin}
      role="region"
      aria-label="Platform details"
      className="flex h-full w-full flex-col bg-bg-base"
    >
      <Header
        title={title}
        subtitle={paneSubtitle(`Platform · ${producerId.slice(0, 8)}…`, origin)}
        actions={<SectionVisibilityMenu entityType="platform" />}
      />
      <div data-testid="entity-details-body" className="min-h-0 flex-1 overflow-y-auto">
        <PlatformSections producerId={producerId} />
      </div>
    </div>
  );
}

/**
 * System Status **component** Entity Details container (ADR-024 §4 amended
 * 2026-07-24; REQ-STATUS-027). Resolves the selected topology node against the
 * shared **live** feed (:func:`useResolvedSystemStatus`) — deduped with the
 * System Status panel's own snapshot subscription, so the pill here matches the
 * topology it was selected from (:func:`resolveNodeRender`; never a false green,
 * CLAUDE.md §8.1). Wrapped in ``system-status-scope`` so the panel-scoped status
 * tokens (the OQ-5 amber, the legible standby / unknown greys — globals.css)
 * carry into Entity Details (Flow L §8; REQ-UI-046).
 *
 * Also the second entry point into a ``status-interface`` focus (the first being
 * an icon on the diagram): its Interfaces section selects one, which is where
 * REQ-STATUS-040's ``+K more`` overflow affordance lands the operator.
 */
function StatusComponentDetailPanel({ nodeId }: { nodeId: string }): React.ReactElement {
  const model = useResolvedSystemStatus();
  const { inspectStatus, clear } = useSelectionActions();
  const node = TOPOLOGY_NODES.find((candidate) => candidate.id === nodeId) ?? null;

  if (node === null) {
    // A selection referencing a node this build does not know (drift): an honest
    // empty state, never a blank panel (CLAUDE.md §8.1).
    return <EmptyState />;
  }

  const render = resolveNodeRender(node, model.byKind);
  // A v2 node can be backed by several KINDS (one Peer Manager node covers all three
  // egress kinds; one Ingress node covers both ingress kinds), so the instances
  // behind it are the union — not one kind's list.
  const components: readonly ResolvedComponent[] = node.kinds.flatMap(
    (kind) => model.byKind.get(kind) ?? [],
  );
  const primary = primaryComponent(components, render.state);
  // ⚠ The first-of-kind scoping fix (REQ-STATUS-039 Notes): the roster follows
  // the instance the sections describe, never `byKind.get(kind)[0]`.
  const rosterComponentId = componentRosterId(node, primary);
  const asOf = primary ? formatStatusTimestamp(primary.generated_at) : null;
  // The plane is NOT surfaced here (architect, 2026-08-01). "Data plane" /
  // "Control plane" is Meridian-internal architecture vocabulary that means
  // nothing to an operator looking at a component's detail — it labelled a
  // grouping the diagram no longer draws, so on this pane it was a caption for
  // something the reader cannot see. The `plane` attribute survives on the node
  // and the BFF still computes `PlaneRollupModel`; it simply has no operator-
  // facing caption. The subtitle now carries only the freshness stamp, which is
  // the thing an operator actually acts on.
  const subtitle = asOf !== null ? `as of ${asOf}` : "";
  const loading = model.snapshot === undefined && model.query.isLoading;

  return (
    <div
      data-testid="entity-details-panel"
      data-kind="status-component"
      role="region"
      aria-label="System status component details"
      className="system-status-scope flex h-full w-full flex-col bg-bg-base"
      onKeyDown={(event) => {
        // Esc closes the status detail (storyboard §9); the operator returns to
        // the topology via its own roving-tabindex focus.
        if (event.key === "Escape") clear();
      }}
    >
      <Header
        title={node.name}
        subtitle={subtitle}
        meta={
          primary === undefined ? undefined : (
            <p
              className="mt-0.5 truncate font-mono text-meta text-text-muted"
              data-testid="status-process-name"
              title={primary.component_id}
            >
              {primary.component_id}
            </p>
          )
        }
        actions={
          node.planned ? undefined : (
            <StatusPill
              state={render.state}
              // The fault cause rides this badge's tooltip since the "Overall"
              // health row that used to carry it was removed as a duplicate of
              // this very badge (architect, 2026-08-01). The badge is the right
              // host — it is the element whose state the cause explains — but the
              // cause had to move BEFORE the row went, or removing a redundant
              // *state* would have quietly taken a non-redundant *reason* with it.
              title={headerRollupTitle(primary, render.state)}
            />
          )
        }
      />
      <div data-testid="entity-details-body" className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <p role="status" className="px-4 py-6 text-body-sm text-text-secondary">
            Loading system status…
          </p>
        ) : (
          <StatusComponentSections
            node={node}
            state={render.state}
            components={components}
            primary={primary}
            rosterComponentId={rosterComponentId}
            // The node's roster now lists PEERS, matching the stack beside it
            // (REQ-STATUS-081); an interface is reached one level in, from the
            // peer that owns it.
            onSelectPeer={(peerName) =>
              inspectStatus({ kind: "status-peer", peerName, parentNodeId: node.id })
            }
          />
        )}
      </div>
    </div>
  );
}

/**
 * System Status **interface** Entity Details container — the middle level of the
 * component → interface → connection hierarchy (ADR-024 §4 amended 2026-07-31;
 * REQ-STATUS-039).
 *
 * Joins the two sources the hierarchy needs: the **config roster** for
 * existence, and the **live status feed** for health, on the interface name
 * (:func:`interfaceComponent`). The join is a left join — an interface that is
 * configured but not yet reporting renders with an honest ``unknown`` pill, and
 * an interface the config roster no longer lists still renders whatever the feed
 * reports rather than vanishing mid-incident.
 */
function StatusInterfaceDetailPanel({
  selection,
}: {
  selection: StatusInterfaceSelection;
}): React.ReactElement {
  const model = useResolvedSystemStatus();
  const roster = usePeerInterfaces();
  const { inspectStatus } = useSelectionActions();
  const parent = TOPOLOGY_NODES.find((node) => node.id === selection.parentNodeId) ?? null;
  const configuredView = (roster.data ?? []).find((view) => view.name === selection.interfaceName);
  const configured: ConfiguredInterface | null =
    configuredView === undefined
      ? null
      : {
          name: configuredView.name,
          typeId: configuredView.type_id,
          // Privileged, so `undefined` for a field-limited session — carried
          // through as-is rather than defaulted, because REQ-UI-149 needs
          // "not visible to this session" to stay distinguishable from
          // "direct".
          terminal: configuredView.terminal,
        };
  const component = interfaceComponent(model.merged, selection.interfaceName);
  const state = component === undefined ? "unknown" : parseRollupState(component.rollup_state);
  const asOf = component ? formatStatusTimestamp(component.generated_at) : null;
  const back = (): void => inspectStatus({ kind: "status-component", id: selection.parentNodeId });

  return (
    <div
      data-testid="entity-details-panel"
      data-kind="status-interface"
      role="region"
      aria-label="System status interface details"
      className="system-status-scope flex h-full w-full flex-col bg-bg-base"
      onKeyDown={(event) => {
        // Esc steps back to the hosting node first (storyboard §9), matching the
        // connection pane — one level per press, never straight to nothing.
        if (event.key === "Escape") back();
      }}
    >
      <Header
        title={selection.interfaceName}
        subtitle={`Interface · ${parent?.name ?? "unknown node"}${asOf !== null ? ` · as of ${asOf}` : ""}`}
        meta={
          component === undefined ? undefined : (
            <p
              className="mt-0.5 truncate font-mono text-meta text-text-muted"
              data-testid="status-process-name"
              title={component.component_id}
            >
              {component.component_id}
            </p>
          )
        }
        // Same move as the component pane: this pane's "Overall" row was this
        // badge's value rendered twice, so the row went and the cause it carried
        // came here. A direct peer interface reports as its own component, so
        // `component` IS the thing the badge describes — the primary/fallback
        // mismatch the component pane guards against cannot arise here.
        actions={<StatusPill state={state} title={interfaceRollupTitle(component, state)} />}
      />
      <div data-testid="entity-details-body" className="min-h-0 flex-1 overflow-y-auto">
        <StatusInterfaceSections
          interfaceName={selection.interfaceName}
          configured={configured}
          component={component}
          parentLabel={parent?.name ?? "the node"}
          onBack={back}
        />
      </div>
    </div>
  );
}

/**
 * System Status **peer** Entity Details container (ADR-024 §4 amended
 * 2026-08-05; ADR-039 §D-1; REQ-STATUS-081 / -086).
 *
 * Resolves the peer three ways and joins them here rather than in the sections:
 * the **roster** (``GET /api/admin/peers``, authoritative for existence and the
 * source of ``interface_count``), the **status feed** under the namespaced
 * ``peer:<name>`` component id, and the peer's **interfaces** from the one
 * nested read (``GET …/{peer_id}/interfaces``).
 *
 * ⚠ The interface read needs a ``peer_id``, and the selection carries a *name* —
 * deliberately, since the name is what the operator sees and what the icon is
 * labelled with. The roster supplies the id, and it is already cached: the
 * System Status panel holds the same query open to compose the icon stack, so
 * TanStack Query dedups this to zero extra requests (REQ-UI-035).
 *
 * A peer the roster no longer lists still renders whatever the feed reports
 * rather than vanishing mid-incident — the same left-join direction the stack
 * composes by, and the same reason.
 */
function StatusPeerDetailPanel({
  selection,
}: {
  selection: StatusPeerSelection;
}): React.ReactElement {
  const model = useResolvedSystemStatus();
  const roster = usePeers();
  const { inspectStatus } = useSelectionActions();
  const session = useSession();
  const parent = TOPOLOGY_NODES.find((node) => node.id === selection.parentNodeId) ?? null;
  const configured = (roster.data ?? []).find((view) => view.name === selection.peerName) ?? null;
  const interfaceQuery = usePeerInterfacesFor(configured?.peer_id ?? null);
  // REQ-UI-154's bounded mutation subset. The hooks live here rather than in the
  // presentational pane so the pane stays testable without a query client, and
  // so this container remains the one place the peer surface's cache
  // invalidation is wired.
  const saveInterface = useSaveNestedInterface();
  const deleteInterface = useDeleteNestedInterface();
  const deletePeer = useDeletePeer();
  const exportPeer = useExportPeerBundle();
  const [configureOpen, setConfigureOpen] = useState(false);
  const mayMutate = session.hasAnyRole(PEER_MUTATOR_ROLES);
  const component = model.merged.find(
    (c) => c.component_id === peerComponentId(selection.peerName),
  );
  // Left join: unreported means "not yet reporting", not "healthy". The one
  // exception is a peer configured with ZERO interfaces, which never reports at
  // all and is a fault rather than uncertainty — the same reading the collector's
  // own `peer_rollup` gives it, so the two levels agree (REQ-STATUS-086 Notes).
  const state: RollupState =
    component !== undefined
      ? parseRollupState(component.rollup_state)
      : configured?.interface_count === 0
        ? "unhealthy"
        : "unknown";
  const asOf = component ? formatStatusTimestamp(component.generated_at) : null;
  const back = (): void => inspectStatus({ kind: "status-component", id: selection.parentNodeId });

  return (
    <div
      data-testid="entity-details-panel"
      data-kind="status-peer"
      role="region"
      aria-label="System status peer details"
      className="system-status-scope flex h-full w-full flex-col bg-bg-base"
      onKeyDown={(event) => {
        // Esc steps back to the hosting node first (storyboard §9) — one level
        // per press, never straight to nothing.
        if (event.key === "Escape") back();
      }}
    >
      <Header
        title={selection.peerName}
        subtitle={`Peer · ${parent?.name ?? "unknown node"}${asOf !== null ? ` · as of ${asOf}` : ""}`}
        actions={<StatusPill state={state} />}
      />
      <div data-testid="entity-details-body" className="min-h-0 flex-1 overflow-y-auto">
        <StatusPeerSections
          peerName={selection.peerName}
          configured={configured}
          component={component}
          state={state}
          interfaces={joinPeerInterfaces(interfaceQuery.data ?? [], model.merged)}
          interfacesLoading={interfaceQuery.isLoading}
          interfacesError={interfaceQuery.error}
          parentLabel={parent?.name ?? "the node"}
          onBack={back}
          // The peer travels onto the interface selection so that pane can name
          // the full path and step back HERE rather than skipping to the node.
          onSelectInterface={(interfaceName) =>
            inspectStatus({
              kind: "status-interface",
              interfaceName,
              parentNodeId: selection.parentNodeId,
              peerName: selection.peerName,
            })
          }
          // Absent as a WHOLE for a non-privileged session, and absent rather
          // than disabled (REQ-UI-143). The peer must also be in the roster:
          // without its id there is no nested route to address, and a control
          // that cannot act is worse than one that is not offered.
          actions={
            mayMutate && configured !== null
              ? {
                  onSetInterfaceEnabled: (interfaceId, enabled) => {
                    const row = (interfaceQuery.data ?? []).find(
                      (view) => view.interface_id === interfaceId,
                    );
                    // A field-limited row cannot produce a write body; the
                    // buttons do not render for one either (defence in depth).
                    if (row === undefined || row.metadata === undefined) return;
                    saveInterface.mutate({
                      peerId: configured.peer_id,
                      config: withEnabled(row, enabled),
                    });
                  },
                  onDeleteInterface: (interfaceId) =>
                    deleteInterface.mutate({ peerId: configured.peer_id, interfaceId }),
                  onDeletePeer: () =>
                    deletePeer.mutate(configured.peer_id, {
                      // The peer is gone, so this pane has nothing left to
                      // describe — step back to the node that hosts it rather
                      // than leaving a detail pane for an entity that no longer
                      // exists.
                      onSuccess: back,
                    }),
                  onExportPeer: () =>
                    exportPeer.mutate({
                      peerId: configured.peer_id,
                      peerName: configured.name,
                    }),
                  onConfigure: () => setConfigureOpen(true),
                  exportPending: exportPeer.isPending,
                }
              : undefined
          }
        />
      </div>

      {/* Everything beyond the subset opens the full configuration surface,
          pre-selected on this peer — so the status display never becomes a
          second place the same configuration is authored. */}
      <PeerManagementDialog
        open={configureOpen}
        onOpenChange={setConfigureOpen}
        initialSelectionName={selection.peerName}
      />
    </div>
  );
}

/**
 * System Status **connection** Entity Details container (ADR-024 §5 amended
 * 2026-07-24; REQ-STATUS-027; Flow L §5). Resolves the connection from the live
 * per-component list (:func:`useComponentConnections`, the same source the roster
 * drills from — no per-connection BFF route, decision 4). A connection that has
 * aged out resolves to an honest empty state with a back step, never a frozen row.
 */
function StatusConnectionDetailPanel({
  selection,
}: {
  selection: StatusConnectionSelection;
}): React.ReactElement {
  const query = useComponentConnections(selection.componentId);
  const { inspectStatus } = useSelectionActions();
  const connection = query.data?.connections.find((c) => c.connection_id === selection.id) ?? null;
  const backNode = TOPOLOGY_NODES.find((candidate) => candidate.id === selection.nodeId) ?? null;
  const backLabel = backNode?.name ?? "component";
  const back = (): void => inspectStatus({ kind: "status-component", id: selection.nodeId });

  return (
    <div
      data-testid="entity-details-panel"
      data-kind="status-connection"
      role="region"
      aria-label="System status connection details"
      className="system-status-scope flex h-full w-full flex-col bg-bg-base"
      onKeyDown={(event) => {
        // Esc steps back to the owning component first (storyboard §9).
        if (event.key === "Escape") back();
      }}
    >
      <Header
        title={connection?.peer_label ?? "Connection"}
        subtitle={
          connection !== null
            ? `${connection.direction} connection`
            : `Connection · ${shortId(selection.id, "CONN")}`
        }
        actions={
          connection !== null ? (
            <StatusPill state={parseRollupState(connection.rollup_state)} />
          ) : undefined
        }
      />
      <div data-testid="entity-details-body" className="min-h-0 flex-1 overflow-y-auto">
        {query.isLoading ? (
          <p role="status" className="px-4 py-6 text-body-sm text-text-secondary">
            Loading connection…
          </p>
        ) : connection === null ? (
          <div className="px-4 py-6">
            <button
              type="button"
              onClick={back}
              data-testid="connection-back"
              className="mb-2 text-body-sm text-meridian-action hover:underline"
            >
              ‹ Back to {backLabel}
            </button>
            <p className="text-body-sm text-text-muted" data-testid="connection-gone">
              This connection is no longer listed — it may have closed or aged out.
            </p>
          </div>
        ) : (
          <StatusConnectionSections
            connection={connection}
            componentId={selection.nodeId}
            backLabel={backLabel}
            onBack={back}
          />
        )}
      </div>
    </div>
  );
}

function EmptyState(): React.ReactElement {
  return (
    <div
      data-testid="entity-details-empty"
      role="region"
      aria-label="Entity details — no selection"
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-base px-6 text-center"
    >
      <p className="text-body text-text-primary">Nothing selected</p>
      <p className="text-body-sm text-text-muted">
        Select a platform, aggregator, detection, track, alert, or query result to view details.
      </p>
    </div>
  );
}

interface HeaderProps {
  title: string;
  subtitle: string;
  /**
   * Identity line under the freshness stamp — the *what is actually running*
   * facts, as distinct from the title's functional name (architect, 2026-08-01).
   *
   * The System pane uses it for the process name. That fact used to be reachable
   * only as the label of a "Reporting components" row, which meant an operator
   * had to read a list to learn the one thing that list was for.
   */
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}

/**
 * The header badge's hover text — the **fault cause**, or nothing.
 *
 * This moved here on 2026-08-01 when the "Overall" health row was removed as a
 * duplicate of this badge. The row's *state* was genuinely redundant — the pane
 * is scoped to the component whose rollup won the node's stoplight
 * (:func:`primaryComponent`), so "Overall" and this badge showed one value by
 * construction — but the row also carried ``rollup_reason``, which this badge did
 * not. Deleting the row without moving the cause would have removed a duplicate
 * state *and* a unique explanation in the same edit.
 *
 * Two guards, both carried over from REQ-STATUS-027's recorded defects:
 *
 *   - **A healthy component gets no tooltip.** ``rollup_reason`` on a healthy
 *     component reads "up and ready", which explains nothing and would put hover
 *     text on every badge in the product (defect (b)).
 *   - **The cause is only quoted when this component is the one the badge
 *     describes.** ``primaryComponent`` falls back to the first reported
 *     component when none matches the node state; quoting that component's reason
 *     beside a state it did not produce is defect (a).
 */
/**
 * The interface badge's hover text — the fault cause, or nothing.
 *
 * Separate from :func:`headerRollupTitle` because the guards differ: an
 * interface's pane is scoped to *its own* component, so there is no
 * primary-vs-fallback mismatch to defend against. The healthy-component guard is
 * shared (REQ-STATUS-027 defect (b) — "up and ready" explains nothing).
 */
function interfaceRollupTitle(
  component: ResolvedComponent | undefined,
  state: RollupState,
): string | undefined {
  if (component === undefined || state === "healthy") return undefined;
  const reason = component.rollup_reason.trim();
  return reason === "" ? undefined : reason;
}

function headerRollupTitle(
  primary: ResolvedComponent | undefined,
  nodeState: RollupState,
): string | undefined {
  if (primary === undefined || nodeState === "healthy") return undefined;
  if (parseRollupState(primary.rollup_state) !== nodeState) return undefined;
  const reason = primary.rollup_reason.trim();
  return reason === "" ? undefined : reason;
}

function Header({ title, subtitle, meta, actions }: HeaderProps): React.ReactElement {
  return (
    <header className="flex items-start justify-between border-b border-border px-4 py-3">
      <div className="flex min-w-0 items-start gap-2">
        <BackControl />
        <div className="min-w-0">
          <h2
            className="font-mono text-body font-medium text-text-primary"
            data-testid="entity-details-title"
          >
            {title}
          </h2>
          <p
            className="mt-0.5 text-body-sm text-text-secondary"
            data-testid="entity-details-subtitle"
          >
            {subtitle}
          </p>
          {meta}
        </div>
      </div>
      {actions !== undefined && (
        <div className="flex shrink-0 items-center gap-2 pl-3">{actions}</div>
      )}
    </header>
  );
}

/**
 * The back control — step to the selection the current one was navigated
 * from (REQ-UI-186).
 *
 * # Why it exists
 *
 * Clicking a track's member detection, or a detection's DETECTED BY
 * platform, replaced this pane with no way back. The operator had to
 * re-find the track in a list, and from a Query result, re-run the query
 * that produced it.
 *
 * # Why it renders nothing when there is no history
 *
 * A disabled control that is permanently disabled for most selections is
 * chrome that teaches the operator to ignore it. The history is non-empty
 * only after a link traversal, which is exactly when the affordance means
 * something, so the control appears then and is absent otherwise.
 *
 * # Why it names its target
 *
 * "‹ Back to track TRK-ABCDEF" rather than a bare ``‹``: a chevron makes
 * the operator click to find out where it goes, and on this surface one
 * wrong step can cost a re-run query. The System Status panes' own
 * ``‹ Back to <parent>`` breadcrumbs read the same way and stay as they
 * are — those are a *hierarchy* step (to the hosting node), which is a
 * different thing from this traversal history and still correct on first
 * landing, where there is no history at all.
 */
function BackControl(): React.ReactElement | null {
  const target = useNavigationBackTarget();
  const { back } = useSelectionActions();
  if (target === null) return null;
  const description = describeSelection(target);
  return (
    <button
      type="button"
      onClick={back}
      title={`Back to ${description}`}
      aria-label={`Back to ${description}`}
      data-testid="entity-details-back"
      className="mt-0.5 flex shrink-0 items-center gap-0.5 rounded px-1 py-0.5 text-body-sm text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
    >
      <CaretLeft size={12} weight="bold" aria-hidden="true" />
      <span className="sr-only">Back to {description}</span>
    </button>
  );
}
